import { useCallback, useEffect, useRef, useState } from 'react'
import type { SibValueChangeDto } from '@/api/dto/unionChanges.dto'
import {
  listUnionChanges,
  markUnionChangeSeen,
  undoUnionChangeSeen,
  UNION_CHANGES_CONTRACT,
  UNION_CHANGES_PAGE_SIZE,
  type UnionChangesCapabilities,
} from '@/api/unionChanges.api'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { changeKey, periodRange, type PeriodPreset } from '../unionChanges.utils'

/**
 * How often the list is refreshed in the background while it is open and the tab visible, so
 * marks a colleague sets show up without a reload. Same cadence as the marketplace indicator;
 * this is not real-time synchronisation.
 */
export const UNION_CHANGES_POLL_INTERVAL_MS = 60_000

export interface UseUnionChangesOptions {
  /** Per-SIB history: every request is scoped to this SIB. */
  fixedSibUserId?: string
  capabilities?: UnionChangesCapabilities
  initialPeriod?: PeriodPreset
  /** Rows per backend request. */
  pageSize?: number
  /** Background refresh runs only while enabled. The initial load happens regardless. */
  enabled?: boolean
  pollIntervalMs?: number
}

export interface UseUnionChanges {
  /** Every change of the period loaded so far, in the backend's order: newest first. */
  items: SibValueChangeDto[]
  /** What the backend reports for the period; equals `items.length` once everything is loaded. */
  totalCount: number
  period: PeriodPreset
  setPeriod: (period: PeriodPreset) => void
  /** True until the first page of a user-driven load has arrived. */
  loading: boolean
  /** True while the remaining pages of the period are still being fetched. */
  loadingMore: boolean
  /** True while a background refresh is in flight. */
  refreshing: boolean
  error: string | null
  refetch: () => Promise<void>
  markSeen: (change: SibValueChangeDto) => Promise<void>
  undoSeen: (change: SibValueChangeDto) => Promise<void>
  /** Keys (see `changeKey`) of rows with a seen/undo request in flight. */
  pendingKeys: ReadonlySet<string>
  capabilities: UnionChangesCapabilities
}

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

/**
 * The Union change list: loads every change of the chosen period, page by page like the
 * dashboard loads its SIBs, and carries the team-wide seen mark. Searching, filtering, sorting
 * and paging happen in the browser on top of `items`.
 *
 * A user-driven load shows the first page as soon as it arrives and appends the rest. Background
 * refreshes (poll, tab becoming visible, after a seen/undo) collect all pages first and then
 * replace the rows in one step, and are silent when they fail, so a flaky poll never blanks the
 * list. A response that arrives after a newer request was started is dropped, so a slow request
 * for an old period cannot overwrite the newer result.
 */
export function useUnionChanges(options: UseUnionChangesOptions = {}): UseUnionChanges {
  const {
    fixedSibUserId,
    capabilities = UNION_CHANGES_CONTRACT,
    initialPeriod = 'currentYear',
    pageSize = UNION_CHANGES_PAGE_SIZE,
    enabled = true,
    pollIntervalMs = UNION_CHANGES_POLL_INTERVAL_MS,
  } = options

  const [period, setPeriodState] = useState<PeriodPreset>(initialPeriod)
  const [items, setItems] = useState<SibValueChangeDto[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(() => new Set())

  const requestIdRef = useRef(0)
  const mountedRef = useRef(true)
  const pendingRef = useRef<Set<string>>(new Set())

  const load = useCallback(
    async (mode: 'user' | 'background') => {
      const requestId = ++requestIdRef.current
      const current = () => mountedRef.current && requestId === requestIdRef.current
      if (mode === 'user') {
        setLoading(true)
        setLoadingMore(false)
        setError(null)
      } else {
        setRefreshing(true)
      }

      const { from, to } = periodRange(period)
      const params = { from: from || null, to: to || null, sibUserId: fixedSibUserId || null }

      try {
        const first = await listUnionChanges({ ...params, page: 1, pageSize })
        if (!current()) return

        const total = first.totalElements ?? 0
        const collected = [...(first.items ?? [])]
        const lastPage = Math.max(1, Math.ceil(total / pageSize))

        if (mode === 'user') {
          setItems(collected)
          setTotalCount(total)
          setError(null)
          setLoading(false)
          if (lastPage > 1) setLoadingMore(true)
        }

        for (let page = 2; page <= lastPage; page++) {
          const next = await listUnionChanges({ ...params, page, pageSize })
          if (!current()) return
          const rows = next.items ?? []
          if (rows.length === 0) break
          collected.push(...rows)
          if (mode === 'user') setItems([...collected])
        }

        if (mode === 'background') {
          setItems(collected)
          setTotalCount(total)
          setError(null)
        }
      } catch (e) {
        if (!current()) return
        if (mode === 'user') {
          setError(errorMessage(e, 'Die Änderungen konnten nicht geladen werden.'))
        } else {
          console.warn('Background refresh of the Union change list failed:', e)
        }
      } finally {
        if (current()) {
          setLoading(false)
          setLoadingMore(false)
          setRefreshing(false)
        }
      }
    },
    [period, fixedSibUserId, pageSize]
  )

  useEffect(() => {
    mountedRef.current = true
    void load('user')
    return () => {
      mountedRef.current = false
    }
  }, [load])

  useEffect(() => {
    if (!enabled) return

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return
      void load('background')
    }, pollIntervalMs)

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void load('background')
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, load, pollIntervalMs])

  const setPeriod = useCallback((next: PeriodPreset) => setPeriodState(next), [])

  const refetch = useCallback(() => load('user'), [load])

  const setPending = useCallback((key: string, pending: boolean) => {
    if (pending) pendingRef.current.add(key)
    else pendingRef.current.delete(key)
    setPendingKeys(new Set(pendingRef.current))
  }, [])

  const markSeen = useCallback(
    async (change: SibValueChangeDto) => {
      const key = changeKey(change)
      if (pendingRef.current.has(key)) return
      setPending(key, true)
      try {
        const seen = await markUnionChangeSeen(change.changeType, change.changeId)
        if (!mountedRef.current) return
        // The recorded actor and time come from the backend: if a colleague was first, it is
        // their mark that shows.
        setItems(prev => prev.map(item => (changeKey(item) === key ? { ...item, seen } : item)))
        void load('background')
      } catch (e) {
        if (!mountedRef.current) return
        toast.error(
          'Die Änderung konnte nicht als gesehen markiert werden.',
          errorMessage(e, 'Bitte versuchen Sie es erneut.')
        )
      } finally {
        if (mountedRef.current) setPending(key, false)
        else pendingRef.current.delete(key)
      }
    },
    [load, setPending]
  )

  const undoSeen = useCallback(
    async (change: SibValueChangeDto) => {
      const key = changeKey(change)
      if (pendingRef.current.has(key)) return
      setPending(key, true)
      try {
        await undoUnionChangeSeen(change.changeType, change.changeId)
        if (!mountedRef.current) return
        setItems(prev =>
          prev.map(item => (changeKey(item) === key ? { ...item, seen: null } : item))
        )
        void load('background')
      } catch (e) {
        if (!mountedRef.current) return
        toast.error(
          'Die Gesehen-Markierung konnte nicht entfernt werden.',
          errorMessage(e, 'Bitte versuchen Sie es erneut.')
        )
      } finally {
        if (mountedRef.current) setPending(key, false)
        else pendingRef.current.delete(key)
      }
    },
    [load, setPending]
  )

  return {
    items,
    totalCount,
    period,
    setPeriod,
    loading,
    loadingMore,
    refreshing,
    error,
    refetch,
    markSeen,
    undoSeen,
    pendingKeys,
    capabilities,
  }
}
